import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, IconButton,
  Alert, CircularProgress, Button, Divider, Collapse, Tooltip
} from '@mui/material';
import { ArrowBack, Payment, AccountBalance, Download, Receipt, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function EstadoCuentaProveedor() {
  const navigate = useNavigate();
  const { proveedorId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [estadoCuenta, setEstadoCuenta] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [pagosCache, setPagosCache] = useState({});

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    if (proveedorId && proveedorId !== 'undefined') {
      cargarEstadoCuenta();
    } else {
      setError('ID de proveedor no válido');
      setLoading(false);
    }
  }, [proveedorId]);

  const cargarEstadoCuenta = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/cuentas-pagar/estado-cuenta/${proveedorId}`,
        { headers: getHeaders() }
      );
      setEstadoCuenta(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar estado de cuenta');
    } finally { setLoading(false); }
  };

  const fmt = (v) => `${parseFloat(v || 0).toFixed(2)}`;

  const toggleRow = async (compraId) => {
    const isExpanded = expandedRows[compraId];
    setExpandedRows({ ...expandedRows, [compraId]: !isExpanded });
    
    // Cargar pagos si no están en caché
    if (!isExpanded && !pagosCache[compraId]) {
      try {
        const res = await axios.get(
          `${API_URL}/api/cuentas-pagar/pagos?compraId=${compraId}`,
          { headers: getHeaders() }
        );
        setPagosCache({ ...pagosCache, [compraId]: res.data.data || [] });
      } catch (err) {
        console.error('Error cargando pagos:', err);
      }
    }
  };

  const descargarPDFPago = async (pagoId, referencia) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/cuentas-pagar/pagos/${pagoId}/pdf`,
        { headers: getHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Comprobante_Pago_${referencia || pagoId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error descargando PDF:', err);
      alert('Error al descargar el comprobante de pago');
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  if (error) return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/cuentas-pagar')}>Volver</Button>
      <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
    </Box>
  );

  const resumen = estadoCuenta?.resumen || {};
  const compras = estadoCuenta?.compras || [];
  const provNombre = compras[0]?.proveedor?.razonSocial || compras[0]?.proveedor?.razon_social || proveedorId;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/cuentas-pagar')}><ArrowBack /></IconButton>
        <AccountBalance color="primary" />
        <Box>
          <Typography variant="h5" fontWeight={700}>Estado de Cuenta</Typography>
          <Typography variant="body2" color="text.secondary">{provNombre} — RUC: {compras[0]?.proveedor?.ruc || proveedorId}</Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<Payment />}
          onClick={() => navigate(`/cuentas-pagar/registrar-pago?proveedorId=${proveedorId}`)}>
          Registrar Pago
        </Button>
      </Box>

      {/* Resumen */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card><CardContent>
            <Typography variant="caption" color="text.secondary">Total Comprado</Typography>
            <Typography variant="h5">{fmt(resumen.total_comprado)}</Typography>
            <Typography variant="caption">{resumen.cantidad_compras} compras</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card><CardContent>
            <Typography variant="caption" color="text.secondary">Total Pagado</Typography>
            <Typography variant="h5" color="success.main">{fmt(resumen.total_pagado)}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: resumen.saldo_pendiente > 0 ? '#fff3e0' : '#e8f5e9' }}><CardContent>
            <Typography variant="caption" color="text.secondary">Saldo Pendiente</Typography>
            <Typography variant="h5" color={resumen.saldo_pendiente > 0 ? 'error.main' : 'success.main'}>
              {fmt(resumen.saldo_pendiente)}
            </Typography>
            <Typography variant="caption">{resumen.compras_pendientes} pendientes</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* Tabla de compras */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell width={40}></TableCell>
                <TableCell><strong>Fecha</strong></TableCell>
                <TableCell><strong>Factura</strong></TableCell>
                <TableCell align="right"><strong>Total</strong></TableCell>
                <TableCell align="right"><strong>Pagado</strong></TableCell>
                <TableCell align="right"><strong>Saldo</strong></TableCell>
                <TableCell><strong>Estado</strong></TableCell>
                <TableCell align="center"><strong>Pagos</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {compras.map(c => {
                const isExpanded = expandedRows[c.id];
                const pagos = pagosCache[c.id] || [];
                const tienePagos = parseFloat(c.total_pagado || 0) > 0;
                
                return (
                  <>
                    <TableRow key={c.id} hover>
                      <TableCell>
                        {tienePagos && (
                          <IconButton size="small" onClick={() => toggleRow(c.id)}>
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>{c.fecha_emision || new Date(c.created_at).toLocaleDateString('es-EC')}</TableCell>
                      <TableCell>{c.factura?.numeroComprobante || '—'}</TableCell>
                      <TableCell align="right">{fmt(c.totales?.importeTotal || c.total)}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>{fmt(c.total_pagado)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: c.saldo_pendiente > 0 ? 'error.main' : 'success.main' }}>
                        {fmt(c.saldo_pendiente)}
                      </TableCell>
                      <TableCell>
                        <Chip label={c.estado} size="small"
                          color={c.estado === 'PAGADA' ? 'success' : c.estado === 'PARCIAL' ? 'warning' : 'error'} />
                      </TableCell>
                      <TableCell align="center">
                        {tienePagos && (
                          <Chip 
                            icon={<Receipt />}
                            label={pagos.length || '...'} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            onClick={() => toggleRow(c.id)}
                            sx={{ cursor: 'pointer' }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                    
                    {/* Fila expandible con detalle de pagos */}
                    {tienePagos && (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0, bgcolor: '#fafafa' }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, px: 3 }}>
                              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Receipt fontSize="small" />
                                Pagos Realizados
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell><strong>Fecha</strong></TableCell>
                                    <TableCell><strong>Referencia</strong></TableCell>
                                    <TableCell><strong>Forma de Pago</strong></TableCell>
                                    <TableCell align="right"><strong>Monto</strong></TableCell>
                                    <TableCell><strong>Cuenta</strong></TableCell>
                                    <TableCell align="center"><strong>Comprobante</strong></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {pagos.length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={6} align="center">
                                        <CircularProgress size={20} sx={{ my: 1 }} />
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    pagos.map(p => (
                                      <TableRow key={p.id}>
                                        <TableCell>{new Date(p.fecha).toLocaleDateString('es-EC')}</TableCell>
                                        <TableCell>{p.referencia || '—'}</TableCell>
                                        <TableCell>
                                          <Chip label={p.forma_pago} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>
                                          {fmt(p.monto)}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>
                                          {p.cuenta_contable || '—'}
                                        </TableCell>
                                        <TableCell align="center">
                                          <Tooltip title="Descargar Comprobante">
                                            <IconButton 
                                              size="small" 
                                              color="primary"
                                              onClick={() => descargarPDFPago(p.id, p.referencia)}
                                            >
                                              <Download fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    ))
                                  )}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
